import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Sparkles, Type, Copy, Volume2, RotateCcw, HelpCircle, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { useAppStore } from '@/stores/useAppStore';
import { useSceneController } from '@/presentation/controllers/useSceneController';
import { useAudioController } from '@/presentation/controllers/useAudioController';
import { useVideoController } from '@/presentation/controllers/useVideoController';
import { HamburgerMenu } from '@/presentation/components/HamburgerMenu';
import { SubtitleSettingsModal } from '@/presentation/components/SubtitleSettingsModal';
import { SubtitleGuideModal } from '@/presentation/components/SubtitleGuideModal';
import { SceneSubtitleStyleModal } from '@/presentation/components/SceneSubtitleStyleModal';
import { SceneItem } from '@/presentation/components/SceneItem';
import { TextInputSection } from '@/presentation/components/TextInputSection';
import { ActionButtons } from '@/presentation/components/ActionButtons';
import { MobileActionBar } from '@/presentation/components/MobileActionBar';
import { ImageDropZone } from '@/presentation/components/ImageDropZone';
import { GenerationProgressModal } from '@/presentation/components/GenerationProgressModal';
import { CompletionDialog } from '@/presentation/components/CompletionDialog';
import { ErrorConfirmDialog } from '@/presentation/components/ErrorConfirmDialog';

export function MainPage() {
  const {
    scenes,
    generationStatus,
    isSubtitleSettingsOpen,
    setSubtitleSettingsOpen,
    subtitleSettings,
    globalSubtitleEnabled,
    setGlobalSubtitleEnabled,
    updateSceneSubtitleText,
    toggleSceneSubtitle,
    copyTextToSubtitle,
    copyAllTextsToSubtitles,
    updateSceneSubtitleStyle,
    resetSceneSubtitleStyle,
    resetAllSceneSubtitleStyles,
    completionDialog,
    hideCompletionDialog,
    errorConfirmDialog,
    resolveErrorConfirmDialog,
    cancelGeneration,
  } = useAppStore();

  const [textInput, setTextInput] = useState('');
  // スマートフォン幅で台本欄を開いておくか。適用が通ったら畳んで一覧に場所を譲る
  const [scriptOpen, setScriptOpen] = useState(true);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [editingSubtitleSceneId, setEditingSubtitleSceneId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<'speech' | 'subtitle'>('speech');
  const [subtitleGuideOpen, setSubtitleGuideOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 編集中のシーンを取得
  const editingSubtitleScene = editingSubtitleSceneId
    ? scenes.find((s) => s.id === editingSubtitleSceneId) || null
    : null;

  const videoUrl = useMemo(() => {
    if (!videoBlob) return null;
    const url = URL.createObjectURL(videoBlob);
    return url;
  }, [videoBlob]);

  const sceneController = useSceneController();
  const audioController = useAudioController();
  const videoController = useVideoController();

  // audioControllerの参照を保持（useEffect cleanupで使用）
  const audioControllerRef = useRef(audioController);
  useEffect(() => {
    audioControllerRef.current = audioController;
  });

  // マウスは僅かな移動で、タッチは長押しでドラッグを始める。
  // タッチ側に遅延を置くのは、リストのスクロールと並べ替えを衝突させないため (ADR-0009)
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const sortedScenes = [...scenes].sort((a, b) => a.order - b.order);
  const allAudioDone = scenes.length > 0 && scenes.every((s) => s.audioStatus === 'done');
  const hasAllTexts = scenes.length > 0 && scenes.every((s) => s.text);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = scenes.findIndex((s) => s.id === active.id);
        const newIndex = scenes.findIndex((s) => s.id === over.id);
        const newOrder = arrayMove(scenes, oldIndex, newIndex);
        sceneController.handleReorderScenes(newOrder.map((s) => s.id));
      }
    },
    [scenes, sceneController]
  );

  const handleReset = useCallback(async () => {
    const shouldReset = await sceneController.handleReset(videoUrl);
    if (shouldReset) {
      setVideoBlob(null);
      setTextInput('');
      setScriptOpen(true);
    }
  }, [sceneController, videoUrl]);

  useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
      audioControllerRef.current.cleanupAudio();
    };
  }, [videoUrl]);

  return (
    <>
    <div className="app-layout">
      {/* Header */}
      <header className="app-header animate-fade-in-up">
        <div className="flex items-center gap-3">
          <HamburgerMenu />
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              <span className="w-2 h-2 rounded-full bg-coral-500" />
              <span className="w-2 h-2 rounded-full bg-coral-400" />
              <span className="w-2 h-2 rounded-full bg-coral-300" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-warm-800">MOVEATE</h1>
              <p className="text-xs text-warm-400 -mt-0.5">Create Your Story</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Grid Layout */}
      <div className="main-grid">
        {/* Left Panel - Controls */}
        <aside className="control-panel card animate-slide-in-left stagger-2">
          {/* Text Input Section */}
          <div>
            <div className="section-header">
              <div>
                <h2 className="section-title">テキスト入力</h2>
                <p className="section-subtitle">各シーンのセリフを入力</p>
              </div>
            </div>
            <TextInputSection
              textInput={textInput}
              sceneCount={scenes.length}
              scriptOpen={scriptOpen}
              onTextChange={setTextInput}
              onApplyTexts={() => {
                const applied = sceneController.handleApplyTexts(textInput);
                if (applied && scenes.length > 0) setScriptOpen(false);
              }}
              onAddImages={() => fileInputRef.current?.click()}
              onToggleScript={setScriptOpen}
            />
          </div>

          <div className="hidden lg:block divider divider-coral" />

          {/* Action Buttons: スマートフォン幅では下部バーが担う */}
          <div className="hidden lg:block">
            <div className="section-header">
              <div>
                <h2 className="section-title flex items-center gap-2">
                  <Sparkles size={16} className="text-coral-500" />
                  生成
                </h2>
                <p className="section-subtitle">動画・音声を生成</p>
              </div>
            </div>
            <ActionButtons
              hasAllTexts={hasAllTexts}
              allAudioDone={allAudioDone}
              hasScenes={scenes.length > 0}
              hasVideoUrl={!!videoUrl}
              generationStatus={generationStatus}
              onGenerateAll={() => videoController.handleGenerateAll(videoBlob, setVideoBlob)}
              onGenerateAllAudio={() => audioController.handleGenerateAllAudio()}
              onGenerateVideo={() => videoController.handleGenerateVideo(videoBlob, setVideoBlob)}
              onSaveVideo={() => videoController.handleDeliverVideo(videoBlob)}
              onReset={handleReset}
            />
          </div>
        </aside>

        {/* Right Panel - Workspace */}
        <main className="workspace card animate-slide-in-right stagger-3">
          <div className="section-header">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="min-w-0">
                <h2 className="section-title">
                  シーン一覧
                  {scenes.length > 0 && (
                    <span className="ml-2 badge badge-coral">{scenes.length}</span>
                  )}
                </h2>
                <p className="hidden lg:block section-subtitle">
                  画像をドラッグ&ドロップで並び替え
                </p>
              </div>
              {/* 編集モード切り替えタブ */}
              <div className="flex shrink-0 gap-1 p-1 bg-warm-100 rounded-lg">
                <button
                  onClick={() => setEditMode('speech')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    editMode === 'speech'
                      ? 'bg-white text-coral-600 shadow-sm'
                      : 'text-warm-600 hover:text-warm-800'
                  }`}
                >
                  <Volume2 size={14} />
                  音声
                </button>
                <button
                  onClick={() => setEditMode('subtitle')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    editMode === 'subtitle'
                      ? 'bg-white text-coral-600 shadow-sm'
                      : 'text-warm-600 hover:text-warm-800'
                  }`}
                >
                  <Type size={14} />
                  テロップ
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={sceneController.handleImageUpload}
                // display:none ではなく視覚的に隠す。iOS Safari は display:none の
                // ファイル入力をスクリプトから開けないことがある
                className="sr-only"
              />
              {/* 画像追加はスマートフォン幅では入力カード側に置くため、ここでは出さない */}
              <div className="hidden lg:block">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-tertiary"
                >
                  画像追加
                </button>
              </div>
              <button
                onClick={audioController.handleDownloadAllAudio}
                disabled={!scenes.some((s) => s.audioStatus === 'done' && s.audioUrl)}
                className="btn-accent"
              >
                全音声DL
              </button>

              {/* テロップ全体コントロール */}
              <div className="flex items-center gap-1 sm:pl-2 sm:border-l border-warm-200">
                <Type size={16} className="text-coral-500" />
                <button
                  type="button"
                  role="switch"
                  aria-checked={globalSubtitleEnabled}
                  onClick={() => setGlobalSubtitleEnabled(!globalSubtitleEnabled)}
                  className={`toggle-switch ${
                    globalSubtitleEnabled ? 'toggle-switch-on' : 'toggle-switch-off'
                  }`}
                  title={globalSubtitleEnabled ? 'テロップをオフ' : 'テロップをオン'}
                >
                  <span className={`toggle-knob ${globalSubtitleEnabled ? 'toggle-knob-on' : ''}`} />
                </button>
                <button
                  onClick={() => {
                    copyAllTextsToSubtitles();
                    toast.success('全シーンの読み上げテキストをテロップにコピーしました');
                  }}
                  className="btn-icon"
                  title="全シーンの読み上げテキストをテロップにコピー"
                  disabled={!scenes.some((s) => s.text)}
                >
                  <Copy size={16} />
                </button>
                <button
                  onClick={() => {
                    resetAllSceneSubtitleStyles();
                    toast.success('全シーンのスタイルをデフォルトに戻しました');
                  }}
                  className="btn-icon"
                  title="全シーンのスタイルをデフォルトに戻す"
                  disabled={!scenes.some((s) => s.subtitleStyle)}
                >
                  <RotateCcw size={16} />
                </button>
                <button
                  onClick={() => setSubtitleSettingsOpen(true)}
                  className="btn-icon"
                  title="テロップ設定"
                >
                  <Settings size={16} />
                </button>
                <button
                  onClick={() => setSubtitleGuideOpen(true)}
                  className="btn-icon"
                  title="テロップの使い方"
                >
                  <HelpCircle size={16} />
                </button>
              </div>
            </div>
          </div>

          {scenes.length === 0 ? (
            <ImageDropZone
              isEmpty={true}
              onImageDrop={sceneController.handleImageDrop}
              onImageClick={() => fileInputRef.current?.click()}
            />
          ) : (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sortedScenes.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {sortedScenes.map((scene, index) => (
                      <SceneItem
                        key={scene.id}
                        scene={scene}
                        sceneOrder={index}
                        editMode={editMode}
                        subtitleSettings={subtitleSettings}
                        globalSubtitleEnabled={globalSubtitleEnabled}
                        onDelete={(id) => {
                          // 再生中のシーンを削除する場合はプレビュー音声を停止する
                          if (audioController.playingAudioId === id) {
                            audioController.cleanupAudio();
                          }
                          sceneController.handleDeleteScene(id);
                        }}
                        onPlayAudio={audioController.handlePlayAudio}
                        onDeleteAudio={audioController.handleDeleteAudio}
                        onRegenerateAudio={audioController.handleRegenerateAudio}
                        onUploadAudio={audioController.handleUploadAudio}
                        onDownloadAudio={audioController.handleDownloadAudio}
                        onUpdateText={sceneController.handleUpdateText}
                        onUpdateAdditionalTime={sceneController.handleUpdateAdditionalTime}
                        onUpdateSubtitleText={updateSceneSubtitleText}
                        onToggleSubtitle={toggleSceneSubtitle}
                        onCopyTextToSubtitle={copyTextToSubtitle}
                        onOpenSubtitleStyleModal={setEditingSubtitleSceneId}
                        playingAudioId={audioController.playingAudioId}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              <div className="mt-4">
                <ImageDropZone
                  isEmpty={false}
                  onImageDrop={sceneController.handleImageDrop}
                  onImageClick={() => fileInputRef.current?.click()}
                />
              </div>
            </>
          )}
        </main>
      </div>
    </div>

    {/* スマートフォン幅の生成バー。position: fixed が祖先の transform/filter に
        巻き込まれないよう、.app-layout の兄弟として置く */}
    <MobileActionBar
      hasAllTexts={hasAllTexts}
      allAudioDone={allAudioDone}
      hasScenes={scenes.length > 0}
      hasVideoUrl={!!videoUrl}
      generationStatus={generationStatus}
      onGenerateAll={() => videoController.handleGenerateAll(videoBlob, setVideoBlob)}
      onGenerateAllAudio={() => audioController.handleGenerateAllAudio()}
      onGenerateVideo={() => videoController.handleGenerateVideo(videoBlob, setVideoBlob)}
      onSaveVideo={() => videoController.handleDeliverVideo(videoBlob)}
      onReset={handleReset}
    />

    {/* Modals */}
    <GenerationProgressModal status={generationStatus} onCancel={cancelGeneration} />
    <SubtitleSettingsModal open={isSubtitleSettingsOpen} onClose={() => setSubtitleSettingsOpen(false)} />
    <SubtitleGuideModal open={subtitleGuideOpen} onClose={() => setSubtitleGuideOpen(false)} />
    <SceneSubtitleStyleModal
      open={!!editingSubtitleSceneId}
      scene={editingSubtitleScene}
      subtitleSettings={subtitleSettings}
      onClose={() => setEditingSubtitleSceneId(null)}
      onSave={updateSceneSubtitleStyle}
      onReset={resetSceneSubtitleStyle}
    />

    <CompletionDialog
      open={completionDialog.open}
      onClose={hideCompletionDialog}
      title={completionDialog.title}
      description={completionDialog.description}
    />

    <ErrorConfirmDialog
      open={errorConfirmDialog.open}
      onContinue={() => resolveErrorConfirmDialog(true)}
      onAbort={() => resolveErrorConfirmDialog(false)}
      title={errorConfirmDialog.title}
      description={errorConfirmDialog.description}
    />
    </>
  );
}
